
// ABOUTME: Custom hook for managing referral data and operations with enhanced schema validation
// ABOUTME: Handles fetching, creating, and updating referrals with real-time updates, performance monitoring, and schema cache error resilience

import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useSecurityAudit } from './useSecurityAudit';
import { toast } from '@/hooks/use-toast';
import { traceDBOperation, captureError, logger } from '@/utils/sentry';
import type { Database } from '@/integrations/supabase/types';

type ReferralStatus = Database['public']['Enums']['referral_status'];

// Schema validation function to check if required columns exist
const validateReferralsSchema = async () => {
  try {
    const { data, error } = await supabase
      .from('referrals')
      .select('candidate_phone')
      .limit(1);
    
    if (error && error.message.includes('candidate_phone')) {
      logger.warn("Schema validation failed: candidate_phone column not found in cache", { error: error.message });
      return false;
    }
    
    logger.debug("Schema validation passed: candidate_phone column accessible");
    return true;
  } catch (error) {
    logger.error("Schema validation error", { error });
    return false;
  }
};

export function useReferrals() {
  const { user, profile } = useAuth();
  const { logReferralCreation } = useSecurityAudit();
  const queryClient = useQueryClient();
  const channelRef = useRef<any>(null);
  const [schemaValidated, setSchemaValidated] = useState<boolean | null>(null);

  // Enhanced authentication validation
  const validateAuthentication = () => {
    if (!user?.id) {
      logger.error("Authentication validation failed: No user ID");
      throw new Error('User not authenticated. Please log in to continue.');
    }
    
    if (!user?.email && !profile?.email) {
      logger.error("Authentication validation failed: No user email");
      throw new Error('User email not available. Please check your profile.');
    }
    
    logger.debug("Authentication validation passed", { 
      userId: user.id,
      hasProfile: !!profile,
      hasEmail: !!(user.email || profile?.email)
    });
    
    return true;
  };

  // Validate schema on hook initialization
  useEffect(() => {
    const checkSchema = async () => {
      const isValid = await validateReferralsSchema();
      setSchemaValidated(isValid);
      
      if (!isValid) {
        logger.warn("Schema validation failed on initialization");
        captureError(new Error("Schema cache validation failed for candidate_phone column"), {
          component: 'useReferrals',
          operation: 'schema_validation'
        });
      }
    };

    if (user?.id) {
      checkSchema();
    }
  }, [user?.id]);

  const { data: referrals, isLoading } = useQuery({
    queryKey: ['referrals', user?.id],
    queryFn: async () => {
      if (!user?.id) {
        logger.warn("Skipping referrals fetch: No authenticated user");
        return [];
      }
      
      return traceDBOperation(
        'SELECT',
        'referrals',
        async () => {
          const { data, error } = await supabase
            .from('referrals')
            .select(`
              *,
              job_roles (
                title,
                department,
                bonus_amount
              )
            `)
            .eq('referrer_id', user.id)
            .order('submitted_at', { ascending: false });

          if (error) {
            // Check for schema cache errors
            if (error.message.includes('candidate_phone') && error.message.includes('schema cache')) {
              logger.error("Schema cache error detected during fetch", { 
                error: error.message,
                userId: user.id 
              });
              
              // Attempt to refresh schema and retry once
              await validateReferralsSchema();
              
              // Retry the query once
              const { data: retryData, error: retryError } = await supabase
                .from('referrals')
                .select(`
                  *,
                  job_roles (
                    title,
                    department,
                    bonus_amount
                  )
                `)
                .eq('referrer_id', user.id)
                .order('submitted_at', { ascending: false });

              if (retryError) {
                logger.error("Retry failed after schema validation", { 
                  error: retryError.message,
                  userId: user.id 
                });
                throw retryError;
              }
              
              logger.info("Referrals fetched successfully after retry", { 
                count: retryData?.length || 0,
                userId: user.id 
              });
              
              return retryData || [];
            }
            
            logger.error("Failed to fetch referrals", { 
              error: error.message,
              code: error.code,
              userId: user.id 
            });
            throw error;
          }
          
          logger.info("Referrals fetched successfully", { 
            count: data?.length || 0,
            userId: user.id 
          });
          
          return data || [];
        },
        { userId: user.id }
      );
    },
    enabled: !!user?.id,
    retry: (failureCount, error: any) => {
      // Retry up to 2 times for network/server errors, but not for auth errors
      if (failureCount < 2 && !error?.message?.includes('authentication')) {
        logger.info("Retrying referrals fetch", { 
          attempt: failureCount + 1,
          error: error?.message 
        });
        return true;
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
  });

  const createReferral = useMutation({
    mutationFn: async (referralData: any) => {
      logger.info("Starting referral creation", { 
        candidateName: referralData.candidate_name,
        jobRoleId: referralData.job_role_id 
      });

      // Step 1: Validate authentication
      validateAuthentication();

      // Step 2: Validate schema before proceeding
      const schemaIsValid = await validateReferralsSchema();
      if (!schemaIsValid) {
        logger.error("Schema validation failed before referral creation");
        captureError(new Error("Schema cache error: candidate_phone column not accessible"), {
          operation: 'createReferral',
          userId: user?.id,
          phase: 'schema_validation'
        });
        throw new Error('Database schema issue detected. Please try again in a moment.');
      }

      // Step 3: Server-side validation via Edge Function
      const authHeader = `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`;
      
      try {
        const validationResponse = await fetch(`https://pecgyjvzvqjyjzauutlg.supabase.co/functions/v1/validate-referral`, {
          method: 'POST',
          headers: {
            'Authorization': authHeader,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ referralData }),
        });

        if (!validationResponse.ok) {
          const errorData = await validationResponse.json();
          throw new Error(errorData.details ? errorData.details.join(', ') : errorData.error);
        }
      } catch (validationError: any) {
        logger.error("Server-side validation failed", { 
          error: validationError.message,
          userId: user?.id 
        });
        throw validationError;
      }

      // Step 4: Get referrer email with fallback
      const referrerEmail = profile?.email || user?.email;
      if (!referrerEmail) {
        const error = new Error('Referrer email not available. Please complete your profile.');
        logger.error("Referrer email validation failed", { userId: user?.id });
        throw error;
      }

      // Step 5: Create referral with transaction-like behavior and schema-aware approach
      return traceDBOperation(
        'INSERT',
        'referrals',
        async () => {
          // Prepare base referral data (always included columns)
          const baseData = {
            referrer_id: user.id,
            referrer_email: referrerEmail,
            candidate_name: referralData.candidate_name?.trim(),
            candidate_email: referralData.candidate_email?.toLowerCase()?.trim(),
            candidate_linkedin: referralData.candidate_linkedin?.trim() || null,
            candidate_resume_url: referralData.candidate_resume_url || null,
            relationship: referralData.relationship,
            notes: referralData.notes?.trim() || null,
            job_role_id: referralData.job_role_id,
            job_role: referralData.job_role || null, // Save the job role title
            status: 'submitted' as ReferralStatus,
            bonus_paid: false,
            bonus_amount: 0
          };

          // Add candidate_phone only if schema validation passed
          const sanitizedData = schemaIsValid && referralData.candidate_phone 
            ? { ...baseData, candidate_phone: referralData.candidate_phone?.trim() || null }
            : baseData;

          logger.debug("Inserting referral with sanitized data", { 
            candidateName: sanitizedData.candidate_name,
            candidateEmail: sanitizedData.candidate_email,
            hasPhone: 'candidate_phone' in sanitizedData,
            userId: user.id 
          });

          try {
            const { data: insertedReferral, error: insertError } = await supabase
              .from('referrals')
              .insert(sanitizedData)
              .select()
              .single();

            if (insertError) {
              // Handle schema cache errors specifically
              if (insertError.message.includes('candidate_phone') && insertError.message.includes('schema cache')) {
                logger.error("Schema cache error during insert, retrying without phone", { 
                  error: insertError.message,
                  userId: user.id 
                });
                
                // Retry without candidate_phone if it's causing schema cache issues
                const fallbackData = { ...baseData };
                delete (fallbackData as any).candidate_phone;
                
                const { data: retryData, error: retryError } = await supabase
                  .from('referrals')
                  .insert(fallbackData)
                  .select()
                  .single();

                if (retryError) {
                  logger.error("Fallback insert failed", { 
                    error: retryError.message,
                    userId: user.id 
                  });
                  throw retryError;
                }

                logger.warn("Referral created successfully without phone number due to schema issue", {
                  referralId: retryData.id,
                  userId: user.id
                });

                return retryData;
              }

              logger.error("Failed to insert referral", { 
                error: insertError.message,
                code: insertError.code,
                hint: insertError.hint,
                details: insertError.details,
                userId: user.id,
                sanitizedData 
              });
              
              // Provide user-friendly error messages based on error type
              if (insertError.code === '23505') {
                throw new Error('This referral already exists. Please check for duplicates.');
              } else if (insertError.code === '23503') {
                throw new Error('Invalid job role or user reference. Please refresh and try again.');
              } else if (insertError.code === '42501') {
                throw new Error('Permission denied. Please check your authentication and try again.');
              } else if (insertError.message?.includes('violates row-level security')) {
                throw new Error('Authentication error. Please log out and log back in.');
              } else {
                throw new Error(`Failed to create referral: ${insertError.message}`);
              }
            }

            // Step 6: Create activity log (non-critical, shouldn't fail referral creation)
            try {
              const { error: activityError } = await supabase
                .from('referral_activity')
                .insert([{
                  referral_id: insertedReferral.id,
                  activity_type: 'referral_submitted',
                  description: `Referral submitted for ${sanitizedData.candidate_name}`,
                }]);

              if (activityError) {
                logger.warn("Failed to create activity log", { 
                  error: activityError.message,
                  referralId: insertedReferral.id 
                });
              } else {
                logger.debug("Activity log created successfully", { 
                  referralId: insertedReferral.id 
                });
              }
            } catch (activityLogError) {
              logger.warn("Activity log creation failed", { 
                error: activityLogError,
                referralId: insertedReferral.id 
              });
            }

            logger.info("Referral created successfully", {
              referralId: insertedReferral.id,
              candidateName: sanitizedData.candidate_name,
              referrerEmail: referrerEmail,
              userId: user.id
            });

            return insertedReferral;
          } catch (dbError: any) {
            // Additional error handling for unexpected database errors
            logger.error("Unexpected database error during referral creation", {
              error: dbError.message,
              userId: user.id
            });
            
            captureError(dbError, {
              operation: 'createReferral',
              phase: 'database_insert',
              userId: user?.id
            });
            
            throw dbError;
          }
        },
        { 
          candidateName: referralData.candidate_name,
          referrerEmail: referrerEmail,
          userId: user.id 
        }
      );
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['referrals'] });
      logger.info("Referral mutation successful", { referralId: data.id });
      
      // Log successful referral creation
      logReferralCreation(data.id, true);
      
      toast({
        title: "Success!",
        description: `Referral for ${data.candidate_name} has been submitted successfully.`,
      });
    },
    onError: (error: Error) => {
      logger.error("Referral creation failed", { 
        error: error.message,
        userId: user?.id 
      });
      
      // Log failed referral creation
      logReferralCreation('unknown', false, error.message);
      
      captureError(error, {
        operation: 'createReferral',
        userId: user?.id
      });

      // Show user-friendly error message
      toast({
        title: "Referral Submission Failed",
        description: error.message || "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
    },
    retry: (failureCount, error: any) => {
      // Don't retry schema cache errors - they need manual intervention
      if (error?.message?.includes('schema cache') || error?.message?.includes('candidate_phone')) {
        logger.info("Not retrying schema cache error", { 
          error: error.message 
        });
        return false;
      }
      
      // Only retry for network/server errors, not validation or auth errors
      const retryableErrors = ['network', 'fetch', 'timeout', 'ECONNRESET', 'ETIMEDOUT'];
      const shouldRetry = failureCount < 2 && retryableErrors.some(keyword => 
        error?.message?.toLowerCase()?.includes(keyword)
      );
      
      if (shouldRetry) {
        logger.info("Retrying referral creation", { 
          attempt: failureCount + 1,
          error: error.message 
        });
      }
      
      return shouldRetry;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Set up real-time subscriptions with proper cleanup and duplicate prevention
  useEffect(() => {
    if (!user?.id) return;

    // Clean up any existing channel first
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    // Create a unique channel name to avoid conflicts
    const channelName = `referrals_${user.id}_${Math.random().toString(36).substr(2, 9)}`;

    logger.debug("Setting up real-time channel", { 
      channelName,
      userId: user.id 
    });

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'referrals',
          filter: `referrer_id=eq.${user.id}`,
        },
        (payload) => {
          logger.debug("Real-time referrals update received", { 
            event: payload.eventType,
            userId: user.id 
          });
          queryClient.invalidateQueries({ queryKey: ['referrals'] });
        }
      )
      .subscribe((status) => {
        logger.debug("Real-time subscription status", { 
          status,
          channelName,
          userId: user.id 
        });
      });

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        logger.debug("Cleaning up real-time channel", { 
          channelName,
          userId: user.id 
        });
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [user?.id, queryClient]);

  return {
    referrals,
    isLoading,
    createReferral: createReferral.mutate,
    isCreating: createReferral.isPending,
    error: createReferral.error,
    schemaValidated, // Expose schema validation status
  };
}
